/**
 * The patient dashboard's lens dispensing module.
 *
 * Lists the patient's dispensing jobs - glasses and contact lenses together - with where each one
 * is up to, and opens the job dialog for its pathway. Each job's next step (mark the order sent, the lab
 * accepted it, received, collected) is a button on its row.
 *
 * Shown only to a practice with the lensDispensingFeature, which the API also requires.
 */
app.component("lensDispensingTile", {
	templateUrl: "app/src/js/components/lensDispensing/lensDispensingTile.html",
	controller: "lensDispensingTileController",
	bindings: {
		patientId: "<",
	},
});

app.controller("lensDispensingTileController", [
	"$scope",
	"$rootScope",
	"$mdDialog",
	"$element",
	"$animate",
	"$q",
	"LensDispensingStore",
	"LensPrescribingStore",
	"LensStatusDialog",
	"CurrentPatient",
	"Session",
	"PUSH_EVENTS",
	"toast",
	function ($scope, $rootScope, $mdDialog, $element, $animate, $q, Store, Rx, LensStatusDialog, CurrentPatient, Session, PUSH_EVENTS, toast) {
		const $ctrl = this;
		$animate.enabled($element, false);
		$scope.S = Store;
		$scope.R = Rx;
		$scope.jobs = [];
		$scope.state = { loading: false, error: null };
		// Which pathway a new job takes - asked when the add link is clicked.
		$scope.picker = { open: false };

		const features = (Session.practice && Session.practice.practiceFeatures) || {};
		$scope.enabled = !!features.lensDispensingFeature && Session.hasPermission("CLINICAL_LENS_DISPENSE");

		function load() {
			if (!$ctrl.patientId || !$scope.enabled) return;
			$scope.jobs = Store.getJobs($ctrl.patientId);
			$scope.state = { loading: true, error: null };
			Store.loadPatientJobs($ctrl.patientId)
				.catch(function (resp) {
					$scope.state.error = Store.errorMessage(resp, "The dispensing jobs could not be loaded.");
				})
				.finally(function () {
					$scope.state.loading = false;
				});
		}
		$ctrl.$onChanges = load;
		$scope.reload = load;

		$scope.statusClass = (job) => "status-" + job.status.toLowerCase().replace("_", "-");
		$scope.nextAction = Store.nextActionFor;
		$scope.activeCount = () => $scope.jobs.filter((j) => j.status !== "COLLECTED").length;
		$scope.nextDate = Store.keyDate;

		// A payment or void on one of these jobs' invoices, made in the patient's invoices by anyone, reaches
		// the job on the server, so the jobs are read again.
		[PUSH_EVENTS.INVOICE_PAID, PUSH_EVENTS.INVOICE_UPDATED, PUSH_EVENTS.INVOICE_DELETED].forEach((type) =>
			$scope.$on(type, function (e, pushEvent) {
				const invoiceId = pushEvent && pushEvent.data && pushEvent.data.invoiceId;
				if (invoiceId && $scope.jobs.some((j) => j.invoice && j.invoice.xeroId === invoiceId)) load();
			})
		);

		const fail = (resp, fallback) => toast.create({ timeout: 6000, message: Store.errorMessage(resp, fallback), className: "alert-danger", dismissible: true });

		// `type` picks the pathway for a new job; an existing job opens in its own.
		$scope.openJob = function (job, type) {
			const dialog = Store.dialogFor(job, type);
			$scope.picker.open = false;
			$mdDialog
				.show({
					controller: dialog.controller,
					templateUrl: dialog.templateUrl,
					parent: angular.element(document.body),
					clickOutsideToClose: false,
					escapeToClose: false,
					scope: $scope.$new(true),
					preserveScope: false,
					locals: { existingJob: job || null, jobPatient: null },
				})
				.catch(angular.noop);
		};

		// Moves the job on. Marking the order sent asks for the supplier's reference first, and saves the
		// order PDF to the patient's files; sending or collecting with something unpaid offers to take it.
		$scope.advance = function (job, $event) {
			if ($event) $event.stopPropagation();
			const action = Store.nextActionFor(job);
			if (!action || job.$busy) return;
			const confirmed = LensStatusDialog.needed(job, action) ? LensStatusDialog.show(job, CurrentPatient.patient, action) : $q.resolve({});
			confirmed.then(
				function (result) {
					// A payment taken in the dialog replaced the job.
					const current = result.job || job;
					current.$busy = true;
					return Store.advanceJob($ctrl.patientId, current, { reference: result.reference || null })
						.catch((resp) => fail(resp, "The job could not be moved on."))
						.finally(() => (current.$busy = false));
				},
				angular.noop
			);
		};

		// Puts a mis-clicked status back one step - "Collected" clicked on the wrong job, say.
		$scope.undoStatus = Store.previousStatus;
		$scope.undoLabel = Store.undoLabel;
		$scope.undo = function (job, $event) {
			if ($event) $event.stopPropagation();
			if (!Store.previousStatus(job) || job.$busy) return;
			job.$busy = true;
			Store.revertJob($ctrl.patientId, job)
				.catch((resp) => fail(resp, "The job could not be put back a step."))
				.finally(() => (job.$busy = false));
		};

		// An invoiced job is locked, so its invoice is where payments are taken. One in Xero opens in the
		// patient's invoices, which the patient header shows and says it has; the jobs are read again when it
		// closes. One kept in CatTrax only - or with no header on the page - opens on the job's own invoice card.
		$scope.openInvoice = function (job, $event) {
			if ($event) $event.stopPropagation();
			const xeroId = job.invoice && job.invoice.xeroId;
			if (xeroId && $rootScope.$broadcast("billing:openInvoice", xeroId, load).defaultPrevented) return;
			$scope.openJob(job);
		};

		// Repeat a contact lens supply: a new job with the same prescription and supply, ready to send.
		$scope.reorder = function (job, $event) {
			if ($event) $event.stopPropagation();
			$scope.openJob(Store.reorderFrom(job));
		};

		$scope.removeJob = function (job, $event) {
			if ($event) $event.stopPropagation();
			if (job.$busy) return;
			job.$busy = true;
			Store.removeJob($ctrl.patientId, job)
				.catch((resp) => fail(resp, "The quote could not be deleted."))
				.finally(() => (job.$busy = false));
		};
	},
]);
